import { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  Calendar, 
  Radio, 
  BookOpen,
  ShoppingCart,
  MessageSquare,
  Megaphone,
  Settings,
  ChevronRight,
  Loader2,
  Mail,
  Star,
  Bell,
  Menu,
  X,
  CreditCard
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLayout = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar handler
  const closeSidebar = (e) => {
    if (e) e.stopPropagation();
    setSidebarOpen(false);
  };

  // Open sidebar handler
  const openSidebar = (e) => {
    if (e) e.stopPropagation();
    setSidebarOpen(true);
  };

  useEffect(() => {
    const checkAdmin = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.is_admin) {
          setIsAdmin(true);
          // Fetch stats
          const statsRes = await axios.get(`${API_URL}/admin/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setStats(statsRes.data);
        } else {
          navigate('/');
        }
      } catch (error) {
        navigate('/login');
      } finally {
        setChecking(false);
      }
    };

    if (!authLoading) {
      checkAdmin();
    }
  }, [token, authLoading, navigate]);

  if (authLoading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-100">
        <Loader2 className="w-8 h-8 animate-spin text-saffron-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const navItems = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/admin/products', label: 'Products', icon: Package },
    { to: '/admin/teachers', label: 'Teachers', icon: Users },
    { to: '/admin/events', label: 'Events', icon: Calendar },
    { to: '/admin/live-streams', label: 'Live Streams', icon: Radio },
    { to: '/admin/katha', label: 'Katha', icon: BookOpen },
    { to: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { to: '/admin/requests', label: 'Service Requests', icon: MessageSquare, badge: stats?.pending_requests },
    { to: '/admin/advertisements', label: 'Advertisements', icon: Megaphone, badge: stats?.pending_ads },
    { to: '/admin/messages', label: 'Messages', icon: Mail, badge: stats?.unread_messages },
    { to: '/admin/reviews', label: 'User Reviews', icon: Star, badge: stats?.pending_reviews },
    { to: '/admin/payment-settings', label: 'Payment Settings', icon: CreditCard },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-layout">
      {/* Mobile Menu Button - Only show when sidebar is closed */}
      {!sidebarOpen && (
        <button 
          onClick={openSidebar}
          className="lg:hidden fixed top-4 left-4 z-50 bg-royal-800 text-white p-2 rounded-lg shadow-lg"
          aria-label="Open menu"
          data-testid="mobile-menu-open"
        >
          <Menu className="w-6 h-6" />
        </button>
      )}

      {/* Overlay - Click to close sidebar */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40 cursor-pointer"
          onClick={closeSidebar}
          onTouchEnd={closeSidebar}
          aria-hidden="true"
          data-testid="sidebar-overlay"
        />
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`w-64 bg-royal-800 min-h-screen fixed left-0 top-0 z-50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button inside sidebar for mobile */}
          <button
            onClick={closeSidebar}
            className="lg:hidden absolute top-4 right-4 text-white p-2 hover:bg-royal-700 rounded-lg transition-colors z-50"
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="p-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-saffron-500 rounded-full flex items-center justify-center">
                <span className="text-white font-heading font-bold">ੴ</span>
              </div>
              <div>
                <span className="font-heading font-bold text-white text-lg block">Khalsa Sidak</span>
                <span className="text-royal-300 text-xs">Admin Panel</span>
              </div>
            </Link>
          </div>
          
          <nav className="px-4 space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={closeSidebar}
                className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.to, item.exact)
                    ? 'bg-saffron-500 text-white'
                    : 'text-royal-200 hover:bg-royal-700 hover:text-white'
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </div>
                {item.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>
          
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="bg-royal-700 rounded-lg p-4">
              <p className="text-royal-200 text-sm">Logged in as</p>
              <p className="text-white font-medium truncate">{user?.name}</p>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 p-4 lg:p-8 pt-16 lg:pt-8">
          <Outlet context={{ stats, setStats }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
